/**
 * Which CPF value a save should carry, for the operator doing the saving.
 *
 * Three different people meet at this field and the modal shows each of them a different control:
 * someone who can read the stored CPF edits it in place, someone who may change it without
 * reading it types a replacement into a separate box, and someone with neither permission sees a
 * disabled input. Whatever they are shown, the save has to end up with ONE value.
 *
 * Extracted from handleSave because the reported bug was in the expression itself and nothing
 * could reach it: a síndico fell into the middle branch, where an emptied box reads as "no draft"
 * and falls back to the stored value, so clearing a CPF did nothing at all — silently.
 */
export interface CpfToSubmitInput {
  /** May this operator change the CPF at all? (personModalRules.canEditCPF) */
  canEdit: boolean;
  /** May they READ the stored value? Decides which control they were shown. */
  canViewFull: boolean;
  /** The main form's CPF field — only meaningful when they can read the stored value. */
  formCpf: string;
  /** Has the write-only "Alterar CPF" box been opened? */
  draftActive: boolean;
  /** What they typed into that box. */
  draft: string;
  /** What is on file now. */
  storedCpf?: string | null;
}

export function cpfToSubmit({
  canEdit,
  canViewFull,
  formCpf,
  draftActive,
  draft,
  storedCpf,
}: CpfToSubmitInput): string {
  const stored = storedCpf || '';
  if (!canEdit) return stored;

  // Can read it: the form field IS the value, empty included — that is how a CPF gets cleared.
  if (canViewFull) return formCpf;

  // Cannot read it: only a value they actually typed counts. An empty box is an abandoned edit,
  // not a request to erase a document they cannot see — clearing belongs to whoever can read
  // what they are clearing.
  return draftActive && draft ? draft : stored;
}
